// Pure JS, no Node built-ins — this runs both in the API (dispatch-time
// SSRF checks) and the browser (schema validation shared with the
// frontend), so it can't use `node:net`/`node:dns`.
const IPV4_BLOCKED_RANGES = [
    ["0.0.0.0", 8], // "this network"
    ["10.0.0.0", 8], // RFC1918 private
    ["100.64.0.0", 10], // CGNAT (RFC6598)
    ["127.0.0.0", 8], // loopback
    ["169.254.0.0", 16], // link-local, incl. 169.254.169.254 cloud metadata
    ["172.16.0.0", 12], // RFC1918 private
    ["192.0.0.0", 24], // IETF protocol assignments
    ["192.0.2.0", 24], // TEST-NET-1
    ["192.168.0.0", 16], // RFC1918 private
    ["198.18.0.0", 15], // benchmarking
    ["224.0.0.0", 4], // multicast
    ["240.0.0.0", 4], // reserved
    ["255.255.255.255", 32], // broadcast
];
function ipv4ToUint32(ip) {
    const parts = ip.split(".");
    if (parts.length !== 4)
        return null;
    let result = 0;
    for (const part of parts) {
        if (!/^\d{1,3}$/.test(part))
            return null;
        const octet = Number(part);
        if (octet > 255)
            return null;
        result = ((result << 8) | octet) >>> 0;
    }
    return result;
}
function isBlockedIpv4(ip) {
    const value = ipv4ToUint32(ip);
    if (value === null)
        return false;
    for (const [base, prefixLength] of IPV4_BLOCKED_RANGES) {
        const baseValue = ipv4ToUint32(base);
        if (baseValue === null)
            continue;
        const mask = prefixLength === 0 ? 0 : (0xffffffff << (32 - prefixLength)) >>> 0;
        if ((value & mask) === (baseValue & mask))
            return true;
    }
    return false;
}
function expandIpv6(ip) {
    if (ip.includes(".")) {
        // IPv4-mapped/compatible forms (e.g. "::ffff:127.0.0.1") are handled by
        // the caller before reaching here.
        return null;
    }
    const halves = ip.split("::");
    if (halves.length > 2)
        return null;
    const parseGroup = (s) => (s.length === 0 ? [] : s.split(":"));
    const head = parseGroup(halves[0]);
    const tail = halves.length === 2 ? parseGroup(halves[1]) : [];
    if (halves.length === 1) {
        if (head.length !== 8)
            return null;
        return head;
    }
    const missing = 8 - head.length - tail.length;
    if (missing < 0)
        return null;
    return [...head, ...Array(missing).fill("0"), ...tail];
}
function isBlockedIpv6(ip) {
    const lower = ip.toLowerCase();
    // IPv4-mapped ("::ffff:a.b.c.d") / IPv4-compatible ("::a.b.c.d") — unwrap
    // and re-check the embedded IPv4 address.
    const v4MappedMatch = lower.match(/^::(ffff:)?(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/);
    if (v4MappedMatch)
        return isBlockedIpv4(v4MappedMatch[2]);
    if (lower === "::1" || lower === "::")
        return true;
    const hextets = expandIpv6(lower);
    if (!hextets)
        return false;
    const parsed = hextets.map((h) => parseInt(h || "0", 16));
    if (parsed.some((n) => Number.isNaN(n) || n < 0 || n > 0xffff))
        return false;
    const first = parsed[0];
    // fc00::/7 — unique local addresses
    if ((first & 0xfe00) === 0xfc00)
        return true;
    // fe80::/10 — link-local
    if ((first & 0xffc0) === 0xfe80)
        return true;
    return false;
}
/** True if `ip` is a syntactically-literal IPv4/IPv6 address in a
 * private/loopback/link-local/reserved range that a webhook must not
 * target. */
export function isBlockedIpLiteral(ip) {
    const bare = ip.startsWith("[") && ip.endsWith("]") ? ip.slice(1, -1) : ip;
    return bare.includes(":") ? isBlockedIpv6(bare) : isBlockedIpv4(bare);
}
function isLiteralIpAddress(hostname) {
    const bare = hostname.startsWith("[") && hostname.endsWith("]") ? hostname.slice(1, -1) : hostname;
    return bare.includes(":") ? expandIpv6(bare.toLowerCase()) !== null || /^::(ffff:)?[\d.]+$/i.test(bare) : ipv4ToUint32(bare) !== null;
}
/** True if a webhook must not be dispatched to this hostname without a
 * DNS lookup even being needed — i.e. `localhost` or a literal blocked IP
 * address. Returns false for any other DNS name; that case can only be
 * decided after resolving it (see `resolveSafeAddress` in the API). */
export function isDisallowedWebhookHostname(hostname) {
    const bare = hostname.startsWith("[") && hostname.endsWith("]") ? hostname.slice(1, -1) : hostname;
    if (bare.toLowerCase() === "localhost")
        return true;
    return isLiteralIpAddress(bare) && isBlockedIpLiteral(bare);
}
//# sourceMappingURL=ipRangeCheck.js.map